/**
 * Local audit log for reservations. Stored in localStorage under
 * `casaneo_resa_history`. Keyed by reservation id → list of entries
 * `{ ts, user, kind, path, changes: {field: [old, new]} }`.
 *
 * Not a replacement for a server-side audit trail, but enough for
 * dispute-handling in an agency context ("qui a modifié quoi et quand").
 */
const KEY = "casaneo_resa_history";
const LAST_KEY = "casaneo_last_resa_id";
const MAX_ENTRIES = 30;   // per reservation
const MAX_RESAS = 200;    // total keys retained
const IGNORED_KEYS = new Set(["id", "user_id", "updated_at", "created_at"]);

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const write = (m) => {
  try {
    // Trim old reservations if the map grows unbounded.
    const keys = Object.keys(m);
    if (keys.length > MAX_RESAS) {
      keys
        .sort((a, b) => (m[a][0]?.ts || 0) - (m[b][0]?.ts || 0))
        .slice(0, keys.length - MAX_RESAS)
        .forEach((k) => delete m[k]);
    }
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch { /* quota — silent */ }
};

/** Return log entries for a given reservation (newest first). */
export function getResaHistory(resaId) {
  if (!resaId) return [];
  const m = read();
  return Array.isArray(m[resaId]) ? m[resaId] : [];
}

/** Insert an audit entry. */
export function pushResaHistory(resaId, entry) {
  if (!resaId || !entry) return;
  const m = read();
  const list = Array.isArray(m[resaId]) ? m[resaId] : [];
  list.unshift({ ts: Date.now(), ...entry });
  m[resaId] = list.slice(0, MAX_ENTRIES);
  write(m);
}

// --- axios interceptor helpers ---------------------------------------------

const RES_URL_RE = /\/reservations\/([^/?]+)(\/[^?]*)?/;

function extractResaId(url) {
  if (!url) return null;
  const m = RES_URL_RE.exec(url);
  return m ? m[1] : null;
}

function summariseChanges(data) {
  if (!data || typeof data !== "object") return null;
  const kept = {};
  Object.entries(data).forEach(([k, v]) => {
    if (IGNORED_KEYS.has(k)) return;
    if (v == null) return;
    if (typeof v === "object" && !Array.isArray(v)) return;
    kept[k] = v;
  });
  return Object.keys(kept).length ? kept : null;
}

// Route → human label (used in the history panel timeline).
function labelFor(url, method) {
  const path = url || "";
  if (/\/status\b/.test(path)) return "Changement de statut";
  if (/\/paid\b/.test(path)) return "Marquage encaissement";
  if (/\/payments\b/.test(path)) return "Ajout de paiement";
  if (/\/charge-card\b/.test(path)) return "Débit carte";
  if (/\/send-keys\b/.test(path)) return "Envoi des clés";
  if (/\/invoice\/email\b/.test(path)) return "Envoi de facture";
  if (/\/commission\b/.test(path)) return "Édition finance";
  if (/\/checkout\b/.test(path)) return "Génération lien Stripe";
  if (method === "DELETE") return "Suppression";
  if (method === "POST") return "Création";
  if (method === "PUT") return "Modification globale";
  if (method === "PATCH") return "Modification";
  return "Action";
}

/** Attach the axios interceptor. Call ONCE from api.js. */
export function installResaAudit(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (res) => {
      try {
        const url = res.config?.url || "";
        const method = (res.config?.method || "get").toUpperCase();
        if (!/\/reservations\//.test(url) || method === "GET") return res;
        const resaId = extractResaId(url);
        if (!resaId) return res;
        let body = res.config?.data;
        if (typeof body === "string") {
          try { body = JSON.parse(body); } catch { body = null; }
        }
        const changes = summariseChanges(body);
        const user = (() => {
          try { return JSON.parse(localStorage.getItem("casaneo_user") || "null"); } catch { return null; }
        })();
        pushResaHistory(resaId, {
          user: user?.email || user?.name || "gestionnaire",
          kind: labelFor(url, method),
          path: url.replace(/^https?:\/\/[^/]+/, ""),
          method,
          changes,
        });
      } catch { /* never break the app on audit failure */ }
      return res;
    }
  );
}

// --- Reservation focus tracking (for contextual command palette) ----------

/** Remember the last reservation the user interacted with. */
export function setLastResa(res) {
  if (!res?.id) return;
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify({
      id: res.id,
      guest_name: res.guest_name,
      property_name: res.property_name,
      status: res.status,
      total: res.total_price,
      ts: Date.now(),
    }));
  } catch { /* ignore */ }
}

/** Read the last-focused reservation (< 1h old only). */
export function getLastResa() {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id) return null;
    if (parsed.ts && Date.now() - parsed.ts > 60 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}
